import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import { Button, DatePicker, Form, Input, message, Radio, Select } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getFundingData } from "../../store/reducers/fundingSlice";
import { baseUrl, UserSearchReq } from "../../utils/api/apis";
import { country as countryList } from "../../utils/constants/country";
import "./ScheduledNotifications.scss";

const { TextArea } = Input;

/* -------------------- CKEditor Config -------------------- */
const CKEditorInput = ({ value, onChange }) => (
  <div className="ckeditor-wrapper">
    <CKEditor
      editor={ClassicEditor}
      data={value || ""}
      onChange={(event, editor) => {
        const data = editor.getData();
        onChange(
          data === "<p>&nbsp;</p>" ||
            data === '<p><br data-cke-filler="true"></p>' ||
            data === "<p><br></p>" ||
            data === ""
            ? ""
            : data,
        );
      }}
    />
  </div>
);

const countryOptions = countryList.map((item) => ({
  label: item.country,
  value: item.country,
}));

const ScheduledNotifications = () => {
  const [form] = Form.useForm();
  const channel = Form.useWatch("channel", form);
  const [contentType, setContentType] = useState("text");
  const [loading, setLoading] = useState(false);
  const [emailOpts, setEmailOpts] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [planOptions, setPlanOptions] = useState([]);

  const idToken = useSelector((state) => state.auth.idToken);
  const fundingData = useSelector((state) => state.funding.fundingData);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getFundingData());
  }, [dispatch]);

  useEffect(() => {
    let planArray = [];
    fundingData &&
      fundingData.forEach((item) => {
        planArray.push({ value: item.id, label: item.name });
      });
    setPlanOptions(planArray);
  }, [fundingData]);

  let timeoutId = null;

  const fetchUsers = async (value) => {
    try {
      setIsSearching(true);
      const response = await UserSearchReq(idToken, value);

      if (response?.status < 399) {
        const userArray = response.data.map((item) => ({
          label: item?.email,
          value: item?.id,
        }));
        setEmailOpts(userArray);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsSearching(false);
    }
  };

  const handleOnSearch = (value) => {
    if (value && value.length > 0) {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        fetchUsers(value);
      }, 600);
    } else {
      setEmailOpts([]);
    }
  };

  const handleContentTypeChange = (e) => {
    setContentType(e.target.value);
    form.setFieldsValue({ body: "" });
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const parsedUserIds =
        values?.targetUserIds
          ?.map((id) => parseInt(id, 10))
          ?.filter((id) => !isNaN(id)) || [];

      const parsedLoginIds = values.targetLoginIds
        ? values.targetLoginIds
            .split(/[,\s]+/)
            .map((id) => parseInt(id.trim(), 10))
            .filter((id) => !isNaN(id))
        : [];

      const payload = {
        subject: values.subject,
        body: values.body,
        content_type: values.contentType,
        channel: values.channel,
        scheduled_at: values.scheduled_at ? values.scheduled_at.toISOString() : null,
      };

      if (values.channel === "dashboard") {
        payload.target_user_ids = parsedUserIds;
        payload.country = values.country || null;
        payload.plan_id = values.plan_id || null;
      } else if (values.channel === "mt5" || values.channel === "matchtrade") {
        payload.target_login_ids = parsedLoginIds;
        payload.target_user_ids = parsedUserIds;
      }

      await axios.post(`${baseUrl}auth/notifications/admin/schedule/`, payload, {
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-Type": "application/json",
        },
      });

      message.success("Notification scheduled successfully!");
      form.resetFields();
      setContentType("text");
    } catch (error) {
      message.error(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to schedule notification.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="send-notification-container">
      <div className="header-actions">
        <h1>Scheduled Notifications</h1>
        <Button onClick={() => navigate(-1)}>Back</Button>
      </div>    

      <div className="form-wrapper">
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{ contentType: "text", channel: "dashboard" }}
        >
          <Form.Item
            label="Subject"
            name="subject"
            rules={[{ required: true, message: "Enter subject" }]}
          >
            <Input size="large" placeholder="Enter notification subject" />
          </Form.Item>

          <Form.Item
            label="Channel"
            name="channel"
            rules={[{ required: true, message: "Select channel" }]}
          >
            <Select size="large" placeholder="Select channel">
              <Select.Option value="dashboard">Dashboard</Select.Option>
              <Select.Option value="mt5">MT5</Select.Option>
              <Select.Option value="matchtrade">MatchTrader</Select.Option>
              <Select.Option value="all">All</Select.Option>
            </Select>
          </Form.Item>

          {(channel === "dashboard" || channel === "mt5" || channel === "matchtrade") && (
            <Form.Item
              label="Target Users"
              name="targetUserIds"
              extra="Search users by email"
            >
              <Select
                mode="multiple"
                size="large"
                placeholder="Search by email"
                filterOption={false}
                onSearch={handleOnSearch}
                options={emailOpts}
                loading={isSearching}
                style={{ width: "100%" }}
              />
            </Form.Item>
          )}

          {channel === "dashboard" && (
            <>
              <Form.Item
                label="Target Country"
                name="country"
              >
                <Select
                  size="large"
                  placeholder="Select a country"
                  options={countryOptions}
                  showSearch
                  optionFilterProp="label"
                  allowClear
                  style={{ width: "100%" }}
                />
              </Form.Item>

              <Form.Item
                label="Target Plan"
                name="plan_id"
              >
                <Select
                  size="large"
                  placeholder="Select a plan"
                  options={planOptions}
                  showSearch
                  optionFilterProp="label"
                  allowClear
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </>
          )}

          {(channel === "mt5" || channel === "matchtrade") && (
            <Form.Item
              label="Target Login IDs"
              name="targetLoginIds"
              rules={[{ required: true, message: "Enter Target Login IDs" }]}
            >
              <Input size="large" placeholder="Enter Target Login IDs" />
            </Form.Item>
          )}

          <Form.Item
            label="Scheduled Date"
            name="scheduled_at"
            rules={[{ required: true, message: "Select scheduled date and time" }]}
          >
            <DatePicker
              showTime
              size="large"
              placeholder="Select date and time"
              style={{ width: "100%" }}
              allowClear
              disabledDate={(current) => current && current.isBefore(dayjs().startOf("day"))}
            />
          </Form.Item>

          <Form.Item label="Content Type" name="contentType">
            <Radio.Group onChange={handleContentTypeChange}>
              <Radio.Button value="text">Plain Text</Radio.Button>
              <Radio.Button value="html">HTML Layout</Radio.Button>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            label="Notification Body"
            name="body"
            rules={[{ required: true, message: "Enter body" }]}
          >
            {contentType === "text" ? (
              <TextArea rows={10} />
            ) : (
              <CKEditorInput key="html-editor" />
            )}
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              size="large"
              block
            >
              Schedule Notification
            </Button>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
};

export default ScheduledNotifications;
